import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { X, Plus, Minus } from 'lucide-react';
import { PListLineDto } from '../../../../../types/picking';

interface ExtendedPickingLine extends PListLineDto {
  isPartiallyPicked?: boolean;
  isFullyPicked?: boolean;
  image?: string;
  requireLocationScan?: boolean;
  requireItemScan?: boolean;
  palletId?: string;
  itemDescription?: string;
  location?: string;
}

interface QuantityConfirmModalProps {
  isOpen: boolean;
  line: ExtendedPickingLine | null;
  proposedQuantity: number;
  onConfirm: (quantity: number) => void;
  onClose: () => void;
}

export const QuantityConfirmModal: React.FC<QuantityConfirmModalProps> = ({
  isOpen,
  line,
  proposedQuantity,
  onConfirm,
  onClose
}) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(proposedQuantity);

  useEffect(() => {
    if (isOpen) {
      setQuantity(proposedQuantity);
    }
  }, [isOpen, proposedQuantity]);

  if (!isOpen || !line) return null;

  const remaining = line.qty - line.qtyPicked;
  const isValid = quantity > 0 && quantity <= remaining;

  const handleIncrement = () => {
    if (quantity < remaining) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      setQuantity(value);
    } else if (e.target.value === '') {
      setQuantity(0);
    }
  };

  const handleConfirm = () => {
    if (isValid) {
      onConfirm(quantity);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg p-4 sm:p-6 sm:max-w-md overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{t('warehouse.modules.picking.confirmPick')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item info */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">{t('fields.itemNo')}</div>
          <div className="font-medium mb-2">{line.itemNo}</div>

          <div className="text-sm text-gray-600 mb-1">{t('warehouse.modules.picking.labels.description')}</div>
          <div className="font-medium mb-3">{line.itemDescription || t('fields.description')}</div>

          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-gray-600">{t('warehouse.modules.picking.picked')}</div>
              <div className="font-semibold text-blue-600">{line.qtyPicked}</div>
            </div>
            <div>
              <div className="text-gray-600">{t('warehouse.modules.picking.labels.ordered')}</div>
              <div className="font-semibold">{line.qty}</div>
            </div>
            <div>
              <div className="text-gray-600">{t('warehouse.modules.picking.labels.remaining')}</div>
              <div className="font-semibold text-orange-600">{remaining}</div>
            </div>
          </div>
        </div>

        {/* Quantity input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('warehouse.modules.picking.pickQuantity')}
          </label>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDecrement}
              disabled={quantity <= 1}
              className="w-12 h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 rounded-lg transition-colors"
            >
              <Minus size={20} />
            </button>

            <input
              type="number"
              min="1"
              max={remaining}
              value={quantity}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              className="flex-1 text-center text-2xl font-semibold p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />

            <button
              onClick={handleIncrement}
              disabled={quantity >= remaining}
              className="w-12 h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 rounded-lg transition-colors"
            >
              <Plus size={20} />
            </button>
          </div>

          {!isValid && (
            <p className="mt-2 text-sm text-red-600">
              {quantity <= 0 ? 'Antall må være større enn 0' : `Antall kan ikke overstige gjenstående (${remaining})`}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium min-h-[44px] order-1"
          >
            Bekreft
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] order-2"
          >
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
};
